import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare,
  PhoneCall,
  Route,
  Antenna,
  Search,
  Plug,
  BarChart3,
  ShieldAlert,
  Layers,
  Building2,
  Headphones,
  Cloud,
  Server,
  Eye,
  ShieldCheck,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";


const whatYouGet = [
  {
    title: "A2P SMS Transit",
    description: "Deliver application-to-person messages through scalable routes with sender ID management, delivery receipts, and throughput optimization.",
    Icon: MessageSquare,
    gradient: "icon-tile-teal",
  },
  {
    title: "Voice Termination (CLI and Non-CLI)",
    description: "Terminate voice traffic globally with direct and partner-based routes, balancing quality, price, and compliance.",
    Icon: PhoneCall,
    gradient: "icon-tile-indigo",
  },
  {
    title: "Real-Time Routing Logic",
    description: "Optimize routing dynamically based on availability, performance, and regulatory constraints for minimal latency.",
    Icon: Route,
    gradient: "icon-tile-blue",
  },
  {
    title: "Direct-to-Carrier Connectivity",
    description: "Leverage direct operator connections for better control, fewer hops, and improved trust scores.",
    Icon: Antenna,
    gradient: "icon-tile-orange",
  },
  {
    title: "Number Intelligence Support",
    description: "Integrate HLR and number validation to identify number status, operator, and line type before sending.",
    Icon: Search,
    gradient: "icon-tile-pink",
  },
  {
    title: "Multi-Protocol Access",
    description: "Access the platform via SMPP, HTTP/REST, SIP, or H.323, built to integrate with your existing infrastructure.",
    Icon: Plug,
    gradient: "icon-tile-green",
  },
  {
    title: "Monitoring & Analytics Dashboard",
    description: "Track delivery rates, ASR, ACD, and latency in real time, with exportable reports for reconciliation and SLAs.",
    Icon: BarChart3,
    gradient: "icon-tile-teal",
  },
  {
    title: "Fraud Prevention & Filtering",
    description: "Anti-spam and grey-route filtering to help maintain compliance and revenue integrity.",
    Icon: ShieldAlert,
    gradient: "icon-tile-indigo",
  },
];

const howCompaniesUse = [
  {
    title: "SMS Aggregation Platforms",
    description: "Route traffic globally across verified, low-latency SMS paths with DLR feedback and compliance-ready configuration.",
    Icon: Layers,
    color: "text-amber",
  },
  {
    title: "MVNOs and MNOs",
    description: "Extend reach and quality through wholesale SMS and voice routes, managing routing logic and traffic profiles centrally.",
    Icon: Building2,
    color: "text-sky",
  },
  {
    title: "Voice Carriers and Call Centers",
    description: "Carry out international voice termination with consistent CLI presentation and jurisdictional flexibility.",
    Icon: Headphones,
    color: "text-lime",
  },
  {
    title: "CPaaS Providers",
    description: "Outsource SMS and voice delivery to a telecom-grade backbone while retaining control over routing and delivery logic.",
    Icon: Cloud,
    color: "text-violet",
  },
];

const steps = [
  "Tell us your use case and traffic profile so we can assess the right setup.",
  "We configure your preferred access method (SMPP, SIP, etc.), assign test routes, and agree on commercial terms.",
  "Start sending traffic. Monitor performance and switch routes in real time.",
  "Our team helps you meet SLA thresholds, optimize costs, and scale traffic smoothly.",
];

const integrationProtocols = [
  {
    title: "SMS Access",
    items: ["SMPP", "HTTP/REST"],
    Icon: MessageSquare,
    gradient: "icon-tile-teal",
  },
  {
    title: "Voice Access",
    items: ["SIP", "H.323"],
    Icon: PhoneCall,
    gradient: "icon-tile-indigo",
  },
  {
    title: "Supporting Services",
    items: ["HLR Lookup", "Number Validation", "Fraud Detection", "DLR Feedback & CDR Reporting"],
    Icon: ShieldCheck,
    gradient: "icon-tile-blue",
  },
];

const whyChoose = [
  {
    title: "Carrier-Grade Infrastructure",
    description: "Built to handle high-volume telecom traffic with redundancy in mind.",
    Icon: Server,
    color: "text-amber",
  },
  {
    title: "Transparent Routing",
    description: "Gain insight into delivery paths, route health, and cost layers, no black-box billing.",
    Icon: Eye,
    color: "text-sky",
  },
  {
    title: "Scalable and Compliant",
    description: "Built to align with global telecom standards and data privacy laws like GDPR.",
    Icon: ShieldCheck,
    color: "text-lime",
  },
];

const faqs = [
  { question: "What is telecom aggregation?", answer: "Telecom aggregation involves routing SMS or voice traffic through a centralized provider that connects to multiple operators and partners to ensure global delivery." },
  { question: "What's the difference between CLI and non-CLI voice?", answer: "CLI (Calling Line Identification) shows the caller ID, while non-CLI routes are typically cheaper but may not display the originating number and can face restrictions in certain regions." },
  { question: "What protocols do you support?", answer: "Yootelco supports SMPP and HTTP for SMS, as well as SIP for voice, with direct carrier connections behind each." },
  { question: "Is my traffic protected against spam and grey routes?", answer: "Yes. Yootelco applies dynamic filtering and origin-based routing to help maintain regulatory and commercial integrity." },
  { question: "Can I test the platform before committing?", answer: "Yes. After an initial conversation about your use case, we can set up test credentials and sample routing for evaluation." },
  { question: "What coverage do you offer?", answer: "Yootelco provides SMS and voice termination across a global network of direct and partner routes. Talk to our team about coverage for your specific destinations." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const IndustriesTelecomCpaas = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="Wholesale Voice & SMS for Telecom & CPaaS | Yootelco"
        description="High-capacity SMS transit and voice termination for operators, MVNOs, CPaaS providers, and aggregators, on global telecom infrastructure."
        path="/industries/telecom-cpaas"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Infrastructure Built for Carriers
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              Wholesale Voice & SMS Termination
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Connect your platform to global telecom infrastructure with Yootelco. High-capacity A2P SMS transit and voice termination for operators, MVNOs, CPaaS providers, and aggregators.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* What You Get With Yootelco */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">What You Get With Yootelco</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 grid-balanced-4">
          {whatYouGet.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col rounded-[18px] border border-border/60 bg-card px-5 py-6 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <span className={`mx-auto flex h-12 w-12 items-center justify-center rounded-[12px] ${item.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[16px] font-bold leading-[1.3] text-foreground">{item.title}</h3>
                <p className="mt-[10px] flex-1 text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* How Companies Use This */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">How Companies Use This</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 grid-balanced-4">
          {howCompaniesUse.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-5 py-6 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} />
                <h3 className="mt-[18px] text-[16px] font-bold leading-[1.3] text-foreground">{item.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* How It Works */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">How It Works</h2>
        </div>
        <div className="mx-auto mt-[32px] grid max-w-[1120px] auto-rows-fr gap-5 md:grid-cols-2 lg:grid-cols-4 grid-balanced-4">
          {steps.map((s, i) => (
            <article key={s} className="flex h-full flex-col rounded-[16px] border border-border/70 bg-secondary/40 p-[24px]">
              <span className="flex h-[40px] w-[40px] flex-none items-center justify-center rounded-full bg-primary text-[15px] font-bold text-primary-foreground shadow-soft">
                {i + 1}
              </span>
              <p className="mt-[16px] text-[14.5px] font-semibold leading-[1.55] text-foreground">{s}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Integration & Protocol Access */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Integration & Protocol Access</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-3">
          {integrationProtocols.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-7 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${item.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <div className="mt-[10px] flex flex-wrap items-center justify-center gap-2">
                  {item.items.map((it) => (
                    <span key={it} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-foreground/80">
                      {it}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Why Choose Yootelco */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Why Choose Yootelco</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {whyChoose.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section className="section-shell">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Pricing</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Our pricing is usage-based and tailored to your traffic profile, routes, and access preferences. Talk to our team for a quote.
          </p>
          <Link
            to="/contact"
            className="mt-[24px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
          >
            Contact Sales
          </Link>
        </div>
      </section>

      {/* CTA Banner */}
            <CTASection
        title="Ready to Build on Yootelco's Infrastructure?"
        description="Talk to our team about wholesale voice, SMS, and SIP trunking for your platform."
        useCase="Telecom & CPaaS"
        showUnderline={false}
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default IndustriesTelecomCpaas;
